/* eslint-disable max-classes-per-file */
import { logger } from '@app/libs/logger';

import Observable from '../Observable';

interface Persistence<T> {
  load(): T | null;
  save(value: T): void;
  remove(): void;
}

class WorkspaceService {
  private workspaceId: string | null;

  private observable = new Observable();

  private persistence: Persistence<string | null>;

  constructor(workspaceIdPersistence: Persistence<string | null>) {
    this.persistence = workspaceIdPersistence;
    this.workspaceId = workspaceIdPersistence.load();
  }

  private loadFromPersistence() {
    const currentWorkspaceId = this.persistence.load();
    if (this.workspaceId !== currentWorkspaceId) {
      this.workspaceId = currentWorkspaceId;
      this.observable.notify();
    }
  }

  hasWorkspaceId() {
    return !!this.workspaceId;
  }

  getWorkspaceId(): string {
    if (!this.hasWorkspaceId()) {
      logger.error('workspace id has not set.');
      throw new Error('workspace id has not set.');
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.workspaceId!;
  }

  setWorkspaceId(id: string) {
    if (id.length === 0) {
      throw new Error('Workspace id is empty.');
    }
    this.persistence.save(id);
    this.loadFromPersistence();
  }

  removeWorkspaceId() {
    this.persistence.remove();
    this.loadFromPersistence();
  }

  subscribe(listener: () => void): () => void {
    return this.observable.subscribe(listener);
  }
}

class SessionStoragePersistence implements Persistence<string | null> {
  private readonly sessionStorageKey = 'modusign__workspace_id';

  save(value: string) {
    window.sessionStorage.setItem(this.sessionStorageKey, value);
  }

  remove() {
    window.sessionStorage.removeItem(this.sessionStorageKey);
  }

  load() {
    return window.sessionStorage.getItem(this.sessionStorageKey) || null;
  }
}

const workspaceService = new WorkspaceService(new SessionStoragePersistence());
export default workspaceService;
